import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    Link,
    IconButton,
    TextField,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';

import AddIcon from '@mui/icons-material/Add';
import WorkHistory from '@mui/icons-material/WorkHistory';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { IPastWork, IWorkProfile } from '../../types/global/helper';
import { useDispatch } from 'react-redux';
import { addCredit, deleteCredit, updateCredit } from '../../redux/talentProfile/action';
import { IAddCredit } from '../../redux/talentProfile/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { CompanySearchSelect } from '../CompanySearchSelect';
import { ICompany } from '../../redux/company/types';
import { CompanyAvatar } from '../Avatar/CompanyAvatar';

interface CreditsTimelineProps {
    themeStyle: ThemeStyle;
    canEdit?: boolean;
    workProfile: IWorkProfile;
}

const SortableCredit = ({
    credit,
    onEdit,
    onDelete,
    isEditing,
    onEditSubmit,
    onEditCancel,
    canEdit,
}: {
    credit: IPastWork;
    onEdit: (credit: IPastWork) => void;
    onDelete: (id: string) => void;
    themeStyle: ThemeStyle;
    isEditing: boolean;
    onEditSubmit: (credit: IPastWork) => void;
    onEditCancel: () => void;
    canEdit?: boolean;
}) => {
    const { themeStyle } = useThemeContext();

    return (
        <Box>
            {isEditing ? (
                <CreditForm credit={credit} onSubmit={onEditSubmit} onCancel={onEditCancel} themeStyle={themeStyle} />
            ) : (
                <Card
                    variant="outlined"
                    sx={{
                        width: '100%',
                        backgroundColor: '#1a1a1a',
                        borderColor: 'rgba(255,255,255,0.06)',
                        borderRadius: '14px',
                        overflow: 'hidden',
                        transition: 'background-color 0.25s ease, border-color 0.25s ease',
                        '&:hover': {
                            backgroundColor: '#20201f',
                        },
                    }}>
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'flex-start', md: 'center' },
                                justifyContent: 'space-between',
                                gap: { xs: 2, md: 3 },
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '10px',
                                        bgcolor: '#0f0f0f',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                    {credit.company && typeof credit.company !== 'string' ? (
                                        <CompanyAvatar company={credit.company} enableDefaultNavigation size="large" />
                                    ) : (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 18,
                                                color: 'rgba(255,255,255,0.5)',
                                    }}>
                                            {credit.title?.charAt(0)?.toUpperCase() || 'C'}
                                </Typography>
                                    )}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: { xs: 18, sm: 20 },
                                            lineHeight: 1.25,
                                            mb: 0.5,
                                        }}>
                                        {credit.title}
                                    </Typography>
                                    {(!!(typeof credit.company === 'string'
                                        ? credit.company
                                        : credit.company?.company_name) ||
                                        !!credit.type) && (
                                <Typography
                                    sx={{
                                                color: theme[themeStyle].light_gray_text,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: 13,
                                                mb: 0.5,
                                            }}>
                                            {typeof credit.company === 'string'
                                                ? credit.company
                                                : credit.company?.company_name}
                                            {!!(typeof credit.company === 'string'
                                                ? credit.company
                                                : credit.company?.company_name) &&
                                                !!credit.type &&
                                                ' • '}
                                            {credit.type}
                                        </Typography>
                                    )}
                                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                        {!!credit.role?.length && (
                                            <Typography
                                                sx={{
                                                    color: 'rgba(255,255,255,0.65)',
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 11,
                                                    letterSpacing: '0.08em',
                                                    textTransform: 'uppercase',
                                                }}>
                                                {credit.role.join(' • ')}
                                            </Typography>
                                        )}
                                        {!!credit.role?.length && !!credit.year && (
                                            <Box
                                                sx={{
                                                    width: 3,
                                                    height: 3,
                                                    borderRadius: '50%',
                                                    bgcolor: 'rgba(255,255,255,0.24)',
                                                }}
                                            />
                                        )}
                                        {!!credit.year && (
                                            <Typography
                                                sx={{
                                                    color: 'rgba(255,255,255,0.65)',
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 11,
                                                    letterSpacing: '0.08em',
                                                    textTransform: 'uppercase',
                                                }}>
                                                {credit.year}
                                </Typography>
                                        )}
                                    </Stack>
                                    {!!credit.link && (
                                <Link
                                    href={credit.link}
                                    target="_blank"
                                    rel="noopener"
                                    sx={{
                                        color: theme[themeStyle].main_blue,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 13,
                                                mt: 0.75,
                                                display: 'inline-block',
                                                '&:hover': { color: theme[themeStyle].highlight },
                                    }}>
                                    View Project
                                </Link>
                                    )}
                                </Box>
                            </Box>
                            {canEdit && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        width: { xs: '100%', md: 'auto' },
                                        justifyContent: { xs: 'flex-end', md: 'flex-start' },
                                    }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onEdit(credit)}
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            bgcolor: '#262626',
                                            width: 48,
                                            height: 48,
                                            borderRadius: '12px',
                                            '&:hover': { color: theme[themeStyle].highlight, bgcolor: '#20201f' },
                                        }}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => onDelete(credit._id)}
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            bgcolor: '#262626',
                                            width: 48,
                                            height: 48,
                                            borderRadius: '12px',
                                            '&:hover': { color: theme[themeStyle].highlight, bgcolor: '#20201f' },
                                        }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

const CreditForm = ({
    credit,
    onSubmit,
    onCancel,
    themeStyle,
    modalView = false,
}: {
    credit?: IPastWork;
    onSubmit: (credit: IPastWork) => void;
    onCancel: () => void;
    themeStyle: ThemeStyle;
    modalView?: boolean;
}) => {
    const [formData, setFormData] = useState<IPastWork>(
        credit || {
            _id: Date.now().toString(),
            title: '',
            year: '',
            role: [],
            link: '',
            type: '',
            description: '',
        },
    );

    const projectTypes = [
        'short film',
        'music video',
        'feature film',
        'photo shoot',
        'student film',
        'TV series',
        'commercial',
        'web series',
        'private work',
        'documentary',
        'theater production',
        'live performance',
        'virtual production',
        'animation',
        'reality show',
        'podcast',
        'design work',
        'animation',
        'color grading',
        'others',
    ];

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const payload: any = {
            ...formData,
        };
        if (formData.company) {
            payload.company = formData.company._id;
        }
        onSubmit(payload);
    };

    const formInner = (
        <Box
            sx={{
                maxWidth: '100%',
                p: modalView ? { xs: 2, sm: 3 } : { xs: 2, sm: 2.5 },
                position: 'relative',
                overflow: 'hidden',
                ...(modalView
                    ? {
                          backgroundColor: '#1a1a1a',
                          borderRadius: '12px',
                          border: '1px solid rgba(255,255,255,0.06)',
                      }
                    : {}),
                '& .MuiTextField-root .MuiOutlinedInput-root': {
                    backgroundColor: '#131313',
                    borderRadius: '10px',
                },
                '& .MuiTextField-root .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.14)',
                },
                '& .MuiTextField-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.24)',
                },
                '& .MuiTextField-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme[themeStyle].highlight,
                },
                '& .MuiTextField-root .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.72)',
                },
                '& .MuiTextField-root .MuiInputLabel-root.Mui-focused': {
                    color: theme[themeStyle].highlight,
                },
            }}>
            {modalView && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -96,
                        right: -96,
                        width: 260,
                        height: 260,
                        borderRadius: '50%',
                        pointerEvents: 'none',
                        backgroundColor: `${theme[themeStyle].highlight}1a`,
                        filter: 'blur(80px)',
                    }}
                />
            )}
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Project Name"
                                value={formData.title}
                                onChange={e => setFormData({ ...formData, title: e.target.value })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CompanySearchSelect
                                value={formData.company}
                                onChange={value => {
                                    // For work place, we only want single selection
                                    const company = Array.isArray(value)
                                        ? (value[0] as ICompany) || null
                                        : (value as ICompany);
                                    setFormData({ ...formData, company: company });
                                }}
                                placeholder="Search select or add a business or company"
                                multiple={false}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#131313',
                                    borderRadius: '10px',
                                    '& fieldset': { border: 'none' },
                                    '&:hover fieldset': { border: 'none' },
                                    '&.Mui-focused fieldset': { border: 'none' },
                                    '&.Mui-focused': {
                                        boxShadow: `0 0 0 1px ${theme[themeStyle].highlight}`,
                                    },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(255,255,255,0.46)',
                                    opacity: 1,
                                },
                                '& .MuiInputLabel-root': { display: 'none' },
                            }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Year"
                                value={formData.year}
                                onChange={e => setFormData({ ...formData, year: e.target.value })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Project Type"
                                value={formData.type}
                                onChange={e => setFormData({ ...formData, type: e.target.value })}
                                required
                            InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                    native: true,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiSelect-select': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiSelect-icon': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}>
                                <option value="" disabled>
                                    Select a type
                                </option>
                                {projectTypes.map(type => (
                                    <option key={type} value={type}>
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Role"
                                value={formData.role}
                                onChange={e => setFormData({ ...formData, role: [e.target.value] })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                multiline
                                rows={1}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Project Link"
                                value={formData.link}
                                onChange={e => setFormData({ ...formData, link: e.target.value })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={onCancel}
                                    sx={{
                                    borderColor: 'rgba(255,255,255,0.24)',
                                    backgroundColor: '#20201f',
                                        color: theme[themeStyle].primary_white,
                                    borderRadius: 999,
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 0.65, sm: 1 },
                                    minHeight: { xs: 34, sm: 36 },
                                    fontSize: { xs: '0.78rem', sm: '0.9rem' },
                                        '&:hover': {
                                        borderColor: 'rgba(255,255,255,0.34)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                        },
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    sx={{
                                        color: theme[themeStyle].black,
                                    borderRadius: 999,
                                    px: { xs: 2, sm: 2.5 },
                                    py: { xs: 0.75, sm: 1.1 },
                                    minHeight: { xs: 34, sm: 44 },
                                    fontSize: { xs: '0.86rem', sm: '0.95rem' },
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    background: `linear-gradient(90deg, ${theme[themeStyle].highlight} 0%, ${theme[themeStyle].primary_orange} 100%)`,
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            opacity: 0.9,
                                        },
                                    }}>
                                {credit ? 'Update Credits' : 'Add New Credits'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
        </Box>
    );

    if (modalView) {
        return formInner;
    }

    return (
        <Card
            variant="outlined"
            sx={{
                width: '100%',
                mb: 3,
                maxWidth: '100%',
                backgroundColor: '#1a1a1a',
                borderColor: 'rgba(255,255,255,0.06)',
                borderRadius: '14px',
            }}>
            <CardContent sx={{ p: 0 }}>{formInner}</CardContent>
        </Card>
    );
};

const CreditsTimeline: React.FC<CreditsTimelineProps> = ({ themeStyle, canEdit = false, workProfile }) => {
    const [credits, setCredits] = useState<IPastWork[]>(workProfile.credits ?? []);
    const [showForm, setShowForm] = useState(false);
    const [editingCredit, setEditingCredit] = useState<IPastWork | undefined>();
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const dispatch = useDispatch();

    const handleAddCredit = (credit: IPastWork) => {
        setCredits([...credits, credit]);
        setShowForm(false);
        dispatch(
            addCredit({
                ...(credit as unknown as IAddCredit),
                profileId: workProfile._id,
            }),
        );
    };

    const handleEditCredit = (credit: IPastWork) => {
        const index = credits.findIndex(c => c._id === credit._id);
        setEditingCredit(credit);
        setEditingIndex(index);
    };

    const handleUpdateCredit = (credit: IPastWork) => {
        setCredits(credits.map(c => (c._id === credit._id ? credit : c)));
        setEditingCredit(undefined);
        setEditingIndex(null);
        dispatch(updateCredit({ documentId: credit._id, payload: credit }));
    };

    const handleDeleteCredit = (id: string) => {
        setCredits(credits.filter(credit => credit._id !== id));
        const credit = credits.find(credit => credit._id !== id);
        dispatch(
            deleteCredit({
                documentId: id,
                payload: credit,
            }),
        );
    };

    useEffect(() => {
        if (workProfile.credits) setCredits(workProfile.credits);
    }, [workProfile.credits]);

    return (
        <Box sx={{ mx: 'auto', mt: 0, px: { xs: 2, sm: 3 }, mb: 4, maxWidth: 980 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    gap: 2,
                    mb: 4,
                    flexWrap: 'wrap',
                }}>
                <Box>
                    <Typography
                        sx={{
                            color: theme[themeStyle].highlight,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 11,
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            mb: 1,
                        }}>
                        Professional Portfolio
                    </Typography>
                    <Typography
                        sx={{
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 30, sm: 44 },
                            lineHeight: 1.05,
                            letterSpacing: '-0.02em',
                        }}>
                        Experience <span style={{ color: theme[themeStyle].highlight }}>&amp;</span> Credits
                    </Typography>
                </Box>
            {canEdit && credits.length > 0 && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditingCredit(undefined);
                            setShowForm(true);
                        }}
                        sx={{
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].black,
                            borderRadius: '999px',
                            px: { xs: 2.25, sm: 2.75 },
                            py: 1.1,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                opacity: 0.9,
                            },
                        }}>
                        Add Credits
                    </Button>
                )}
                </Box>

            {showForm && !editingCredit && canEdit && (
                <Dialog
                    open={showForm}
                    onClose={() => setShowForm(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '14px',
                            overflow: 'hidden',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid rgba(255,255,255,0.06)',
                            boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
                        },
                    }}>
                    <DialogTitle sx={{ p: 0, bgcolor: '#1a1a1a' }}>
                        <Box
                            sx={{
                                height: 120,
                                width: '100%',
                                position: 'relative',
                                background:
                                    'linear-gradient(135deg, rgba(250,175,63,0.35) 0%, rgba(216,146,34,0.18) 45%, rgba(26,26,26,0.95) 100%)',
                            }}>
                            <IconButton
                                onClick={() => setShowForm(false)}
                    sx={{
                        position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    color: 'rgba(255,255,255,0.7)',
                                    backgroundColor: 'rgba(0,0,0,0.35)',
                                    '&:hover': {
                                        color: theme[themeStyle].tag_white,
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                    },
                                }}>
                                <CloseIcon />
                            </IconButton>
                            <Box sx={{ position: 'absolute', left: 24, right: 24, bottom: 14 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: { xs: 30, md: 42 },
                                        lineHeight: 1.05,
                                        color: theme[themeStyle].tag_white,
                                        mb: 0.75,
                                    }}>
                                    Add New <span style={{ color: theme[themeStyle].highlight }}>Credits</span>
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 14,
                                        color: 'rgba(255,255,255,0.62)',
                                        maxWidth: 860,
                                    }}>
                                    Highlight your role and collaborators
                                </Typography>
                            </Box>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ bgcolor: '#1a1a1a', p: { xs: 2, sm: 3 } }}>
                        <CreditForm
                            onSubmit={handleAddCredit}
                            onCancel={() => setShowForm(false)}
                            themeStyle={themeStyle}
                            modalView
                        />
                    </DialogContent>
                </Dialog>
            )}

            {credits.length === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        py: { xs: 2, sm: 3 },
                        px: { xs: 1, sm: 0 },
                    }}>
                    <Box sx={{ position: 'relative', width: '100%', maxWidth: 672, mx: 'auto' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -96,
                                left: -96,
                                width: 256,
                                height: 256,
                                borderRadius: '50%',
                                bgcolor: alpha(theme[themeStyle].highlight, 0.12),
                                filter: 'blur(120px)',
                                pointerEvents: 'none',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: -96,
                                right: -96,
                                width: 256,
                                height: 256,
                                borderRadius: '50%',
                                bgcolor: alpha(theme[themeStyle].highlight, 0.06),
                                filter: 'blur(100px)',
                                pointerEvents: 'none',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'relative',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: `1px solid ${alpha(theme[themeStyle].tag_white, 0.1)}`,
                                bgcolor: alpha(theme[themeStyle].true_black, 0.55),
                                transition: 'border-color 0.5s ease, box-shadow 0.5s ease',
                                '&:hover': {
                                    borderColor: alpha(theme[themeStyle].tag_white, 0.22),
                                    boxShadow: `0 0 72px ${alpha(theme[themeStyle].highlight, 0.1)}`,
                                },
                            }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    opacity: 0.1,
                                    pointerEvents: 'none',
                                    backgroundImage:
                                        'url(https://www.transparenttextures.com/patterns/carbon-fibre.png)',
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    px: { xs: 4, md: 8 },
                                    py: { xs: 8, md: 10 },
                                }}>
                                <Box sx={{ mb: 4, position: 'relative' }}>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            bgcolor: alpha(theme[themeStyle].highlight, 0.2),
                                            filter: 'blur(24px)',
                                            borderRadius: '50%',
                                            transform: 'scale(1.5)',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: '#262626',
                                            border: `1px solid ${alpha(theme[themeStyle].highlight, 0.22)}`,
                                        }}>
                                        <WorkHistory
                                            sx={{
                                                fontSize: 48,
                                                color: theme[themeStyle].highlight,
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                        fontWeight: 800,
                                        fontSize: { xs: '1.875rem', md: '2.25rem' },
                                        letterSpacing: '-0.03em',
                                        lineHeight: 1.15,
                                        color: theme[themeStyle].primary_white,
                                        mb: 2,
                                    }}>
                                    No experience listed{' '}
                                    <Box component="span" sx={{ color: theme[themeStyle].highlight }}>
                                        yet
                                    </Box>
                                    .
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.65)',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 18,
                                        lineHeight: 1.65,
                                        maxWidth: 448,
                                        mb: canEdit ? 4 : 0,
                                        fontWeight: 500,
                                    }}>
                                    Check back later for updates.
                                </Typography>
                                {canEdit ? (
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setEditingCredit(undefined);
                                            setShowForm(true);
                                        }}
                                        sx={{
                                            backgroundColor: theme[themeStyle].highlight,
                                            color: theme[themeStyle].black,
                                            borderRadius: '999px',
                                            px: { xs: 2.25, sm: 2.75 },
                                            py: 1.1,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            alignSelf: 'center',
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].primary_orange,
                                                opacity: 0.9,
                                            },
                                        }}>
                                        Add Credits
                                    </Button>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Stack spacing={2.5} position="relative">
                    {credits.map((credit, index) => (
                        <SortableCredit
                            key={credit._id}
                            credit={credit}
                            onEdit={handleEditCredit}
                            onDelete={handleDeleteCredit}
                            themeStyle={themeStyle}
                            isEditing={index === editingIndex}
                            onEditSubmit={handleUpdateCredit}
                            onEditCancel={() => {
                                setEditingCredit(undefined);
                                setEditingIndex(null);
                            }}
                            canEdit={canEdit}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export { CreditsTimeline };
